import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState
} from 'react';

import { JDSInputCodeProps } from './generated';
import { useInputState } from '../jdsinputfield/logic';

export interface InputCodeProps
  extends Omit<
    JDSInputCodeProps,
    | 'onFocus'
    | 'onBlur'
    | 'onChange'
    | 'onSubmit'
    | 'onKeydown'
    | 'onPaste'
    | 'onInvalid'
  > {
  /**
   * @deprecated label is not supported now on input code
   */
  label?: string;
  name?: string;
  value?: string;
  placeholder?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
  stateConfig?: {
    errorText?: string;
    successText?: string;
    warningText?: string;
    requiredText?: string;
  };
  onFocus?: (ev: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (ev: React.FocusEvent<HTMLDivElement>) => void;
  onChange?: (ev: ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: () => void;
  required?: boolean;
  onInvalid?: (ev: React.InvalidEvent<HTMLInputElement>) => void;
}

export const withLogic =
  (Component: React.ComponentType<InputCodeProps>): React.FC<InputCodeProps> =>
  (props: InputCodeProps) => {
    const { value: propValue, onChange, ...rest } = props;
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const rootRef = useCallback((element: HTMLDivElement) => {
      if (element) {
        setElement(element);
      }
    }, []);

    const { inputState, stateText, onInvalid } = useInputState(
      props.state,
      props.required,
      props.onInvalid,
      props.stateConfig,
      props.stateText
    );

    const [value, setValue] = useState(propValue?.split('') || []);

    const inputs = Array.from(element?.getElementsByTagName('input') || []);

    useEffect(() => {
      setValue(propValue?.split('') || []);
    }, [propValue]);

    const focusNext = (i: number) => {
      setTimeout(() => {
        inputs[i + 1]?.focus();
        inputs[i + 1]?.select();
      });
    };

    const focusPrev = (i: number) => {
      setTimeout(() => {
        inputs[i - 1]?.focus();
        inputs[i - 1]?.select();
      });
    };

    const handleChange = (
      ev: React.ChangeEvent<HTMLInputElement>,
      i: number
    ) => {
      const newValue = [...value];
      newValue[i] = ev.target.value?.slice(-1) || '';
      setValue(newValue);

      if (props.onChange) {
        const event = {
          ...ev,
          target: {
            ...ev.target,
            value: newValue.join('')
          }
        };
        props.onChange(event);
      }

      if (!isNaN(parseInt(ev.target.value))) {
        focusNext(i);
      }
    };

    const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>, i: number) => {
      const allowedKeys = ['Backspace', 'v', 'ArrowLeft', 'ArrowRight'];
      if (!/^\d$/.test(ev.key) && !allowedKeys.includes(ev.key)) {
        ev.preventDefault();
      } else {
        switch (ev.key) {
          case 'Backspace':
            if (!ev.currentTarget.value) {
              focusPrev(i);
            } else {
              const newValue = [...value];
              newValue[i] = '';
              setValue(newValue);
              if (props.onChange) {
                props.onChange({
                  ...ev,
                  target: {
                    ...ev.target,
                    value: newValue.join('')
                  }
                });
              }
            }
            ev.preventDefault();
            break;
          case 'ArrowLeft':
            focusPrev(i);
            break;
          case 'ArrowRight':
            focusNext(i);
            break;
        }
      }
    };

    const handlePaste = (ev: ClipboardEvent<HTMLInputElement>, i: number) => {
      const clipboardValue = ev.clipboardData
        .getData('text')
        .split('')
        .slice(0, props.codeLength);
      ev.preventDefault();
      const newValue = [...value];
      clipboardValue.forEach((char, idx) => {
        if (i + idx < newValue.length) {
          newValue[i + idx] = char;
        }
      });
      setValue(newValue);
      if (props.onChange) {
        props.onChange({
          ...ev,
          target: {
            ...ev.target,
            value: newValue.join('')
          }
        });
      }
    };

    const codes = Array.from({ length: props.codeLength || 6 }).map((_, i) => ({
      placeholder: props.placeholder?.[i] || (i + 1).toString(),
      value: value[i],
      onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
        handleChange(ev, i),
      onKeyDown: (ev: KeyboardEvent<HTMLInputElement>) => handleKeyDown(ev, i),
      onPaste: (ev: ClipboardEvent<HTMLInputElement>) => handlePaste(ev, i)
    }));

    return (
      <Component
        {...{
          ...rest,
          rootRef,
          state: inputState,
          stateText,
          onInvalid,
          _codes: codes,
          ariaLabel:
            props.ariaLabel || `Input code of length ${props.codeLength}`
        }}
      />
    );
  };
